import { Combobox } from '@/components/Combobox'
import { Input } from '@/components/ui/input'

// US states + DC. Two-letter USPS codes are what gets stored; the full
// name is shown in the list so users find it by typing either part.
const US_STATES: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
]

// Canadian provinces + territories.
const CA_PROVINCES: Array<{ code: string; name: string }> = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
]

const SUBDIVISIONS_BY_COUNTRY: Record<string, Array<{ code: string; name: string }>> = {
  US: US_STATES,
  CA: CA_PROVINCES,
}

export type StateProvinceFieldProps = {
  /** ISO-3166 alpha-2 country code currently selected on the form. */
  country: string | null | undefined
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  placeholder?: string
}

/**
 * Address subdivision picker. Renders a searchable dropdown of US states or
 * Canadian provinces when the matching country is selected; falls back to a
 * plain text input for every other country (and when no country is set yet)
 * so users typing a region for, say, the UK or Germany aren't blocked.
 *
 * Storage stays a free-text TEXT column - we just feed it 2-letter codes
 * for US/CA so the rest of the app (PDF render, customer/vendor list filter)
 * has stable values to work with.
 */
export function StateProvinceField({
  country,
  value,
  onChange,
  disabled,
  placeholder,
}: StateProvinceFieldProps) {
  const subdivisions = country ? SUBDIVISIONS_BY_COUNTRY[country.toUpperCase()] : undefined

  if (!subdivisions) {
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        disabled={disabled}
        placeholder={placeholder ?? 'State / region'}
      />
    )
  }

  const options = subdivisions.map((s) => ({
    value: s.code,
    label: s.name,
    description: s.code,
  }))

  return (
    <Combobox<string>
      value={value || null}
      onChange={(v) => onChange(v ? String(v) : '')}
      options={options}
      placeholder={
        placeholder ?? (country?.toUpperCase() === 'CA' ? 'Pick a province' : 'Pick a state')
      }
      emptyMessage="No matches."
      disabled={disabled}
    />
  )
}
